import { supabase } from '@/lib/supabase';

/**
 * "Show in the menu" settings for the menu items that start hidden (Routines, QC Checklists, Document Acknowledgements).
 *
 * Two database functions, nothing else (supabase/migrations/20261015310000_menu_shell_visibility_v1.sql):
 *   menu_item_settings_read_v1()               approved staff; administrators also get the label, tooltip and last change
 *   menu_item_setting_set_v1(key, shown, note) administrators only; every change is kept in an append-only record
 *
 * A failed read throws: the menu then keeps these items hidden (never shown by accident), and the settings panel says
 * "cannot read" instead of showing a value.
 */

export const MENU_SETTING_FUNCTIONS = {
  read: 'menu_item_settings_read_v1',
  set: 'menu_item_setting_set_v1',
} as const;

/** The navigation item ids that are gated by these settings. Everything not listed here is never affected. */
export const GATED_MENU_KEYS = ['routines', 'qc-checklists', 'doc-acknowledgements'] as const;
export type GatedMenuKey = (typeof GATED_MENU_KEYS)[number];

/** Fired on window after an administrator changes a setting, so the open menu updates without a reload. */
export const MENU_SETTINGS_CHANGED_EVENT = 'menu-item-settings-changed';

export interface MenuItemSettingRow {
  menu_key: string;
  shown: boolean;
  label: string | null;
  tooltip: string | null;
  changed_at: string | null;
  changed_by_name: string | null;
  shown_before: boolean | null;
  change_note: string | null;
}

export class MenuSettingsUnavailableError extends Error {
  constructor() {
    super('Cannot read the menu settings.');
    this.name = 'MenuSettingsUnavailableError';
  }
}

export async function fetchMenuItemSettings(): Promise<MenuItemSettingRow[]> {
  let data: unknown;
  let error: { message?: string | null } | null;
  try {
    ({ data, error } = await supabase.rpc(MENU_SETTING_FUNCTIONS.read));
  } catch {
    throw new MenuSettingsUnavailableError();
  }
  if (error || !Array.isArray(data)) throw new MenuSettingsUnavailableError();
  return data as MenuItemSettingRow[];
}

/** The ids that may be shown right now. Anything not returned as shown stays hidden. */
export function shownMenuKeys(rows: MenuItemSettingRow[]): Set<string> {
  return new Set(rows.filter((row) => row.shown === true).map((row) => row.menu_key));
}

export async function setMenuItemShown(menuKey: string, shown: boolean, note?: string): Promise<void> {
  const { error } = await supabase.rpc(MENU_SETTING_FUNCTIONS.set, {
    p_menu_key: menuKey,
    p_shown: shown,
    p_note: note && note.trim() ? note.trim() : null,
  });
  if (error) throw new Error(error.message || 'The setting could not be saved.');
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(MENU_SETTINGS_CHANGED_EVENT));
}
